import { type LayoutY } from '#packages/layout';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from 'types/documentServices';

import type { SlotData } from './types';

const MOUSE_OFFSET = 20;
const COMP_MARGIN = 20; // margin between components after the slot was opened

const isSlotWithinHeightLimits = (gapHeight: number) =>
  gapHeight >= 0 && gapHeight <= MOUSE_OFFSET * 2;

/**
 * Find component above the slot by looking for the one whose bottomY is the lowest
 */
const getAboveComponent = (
  editorAPI: EditorAPI,
  currentIdx: number,
  compRefs: CompRef[],
) => {
  let aboveComponentIdx = currentIdx;

  const aboveCompLayout =
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
      compRefs[currentIdx],
    );

  const aboveCompBottomY = aboveCompLayout.y + aboveCompLayout.height;

  for (let i = currentIdx + 1; i < compRefs.length; i++) {
    const compRef = compRefs[i];
    const compLayout =
      editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
        compRef,
      );

    // There is a gap, so we are in the territory of belowComponent already
    if (compLayout.y >= aboveCompBottomY) {
      break;
    }

    // If component's bottomY is lower - this is the slot's aboveComponent
    if (compLayout.y + compLayout.height > aboveCompBottomY) {
      aboveComponentIdx = i;
    }
  }

  return aboveComponentIdx;
};

function getAllSlotsForComponent(editorAPI: EditorAPI, comp: CompRef[]) {
  return getAllSlots(
    editorAPI,
    editorAPI.components.getContainer(comp),
    editorAPI.components.getSiblings(comp),
  );
}

function getAllSlotsInContainer(editorAPI: EditorAPI, container: CompRef) {
  return getAllSlots(
    editorAPI,
    container,
    editorAPI.components.getChildren(container),
  );
}

function getAllSlots(
  editorAPI: EditorAPI,
  container: CompRef,
  compRefs: CompRef[],
) {
  const slotData = [] as SlotData[];
  const layoutGet =
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen;

  const compsSortedByY = compRefs.toSorted(
    (a, b) => layoutGet(a).y - layoutGet(b).y,
  );

  const containerLayout = layoutGet(container);
  const containerBottomY = containerLayout.y + containerLayout.height;

  let i = 0;
  while (i < compsSortedByY.length) {
    const aboveComponentIdx = getAboveComponent(editorAPI, i, compsSortedByY);
    const aboveComponent = compsSortedByY[aboveComponentIdx];
    const aboveCompLayout = layoutGet(aboveComponent);
    const aboveCompBottomY = aboveCompLayout.y + aboveCompLayout.height;

    // Slot between container's top edge and first component
    if (
      i === 0 &&
      isSlotWithinHeightLimits(aboveCompLayout.y - containerLayout.y)
    ) {
      slotData.push({
        aboveComponent: null,
        belowComponent: aboveComponent,
        layoutY: {
          y: containerLayout.y,
          height: aboveCompLayout.y - containerLayout.y,
        },
      });
    }

    const belowComponentIdx = aboveComponentIdx + 1;
    const belowComponent = compsSortedByY[belowComponentIdx];

    // If this is the last component, the slot is between it and containers bottom edge
    const slotBottomY = belowComponent
      ? layoutGet(belowComponent).y
      : containerBottomY;

    const slotHeight = slotBottomY - aboveCompBottomY;

    if (isSlotWithinHeightLimits(slotHeight)) {
      slotData.push({
        aboveComponent,
        belowComponent: belowComponent ? belowComponent : null,
        layoutY: {
          y: aboveCompBottomY,
          height: slotHeight,
        },
      });
    }

    i = belowComponentIdx;
  }

  return slotData;
}

function findAvailableSlot(slots: SlotData[], mouseY: number) {
  const isWithin = (a: LayoutY, b: LayoutY) =>
    a.y > b.y && a.y + a.height < b.y + b.height;
  const distanceTo = (a: LayoutY, y: number) => a.y + a.height / 2 - y;
  const getOverlappingArea = (y: number): LayoutY => ({
    y: y - MOUSE_OFFSET,
    height: MOUSE_OFFSET * 2,
  });

  return slots.reduce(
    (closestSlot, currentSlot) => {
      const isCurrentSlotAvailable = isWithin(
        currentSlot.layoutY,
        getOverlappingArea(mouseY),
      );

      if (!isCurrentSlotAvailable) {
        return closestSlot;
      }
      if (!closestSlot) {
        return currentSlot;
      }
      const isCurrentCloser =
        distanceTo(currentSlot.layoutY, mouseY) <
        distanceTo(closestSlot.layoutY, mouseY);

      return isCurrentCloser ? currentSlot : closestSlot;
    },
    null as SlotData | null,
  );
}

/**
 * Push components to make space for slot and position selected component in the middle
 */
function openSlot(
  editorAPI: EditorAPI,
  selectedComp: CompRef | CompRef[],
  slot: SlotData,
) {
  const selectedCompLayout =
    editorAPI.components.layout.measure.getBoundingLayout(selectedComp);

  const container = editorAPI.components.getContainer(selectedComp);
  const containerLayout =
    editorAPI.components.layout.measure.getBoundingLayoutRelativeToScreen(
      container,
    );

  const selectedCompY = slot.layoutY.y - containerLayout.y + COMP_MARGIN;
  const bottomCompY = selectedCompY + selectedCompLayout.height + COMP_MARGIN;

  const positionComponents = () => {
    if (slot.belowComponent) {
      editorAPI.components.layout.moveToAndPush(slot.belowComponent, {
        y: bottomCompY,
      });
    } else {
      editorAPI.components.layout.resizeToAndPush(container, {
        height:
          containerLayout.height +
          selectedCompLayout.height +
          COMP_MARGIN * 2 -
          slot.layoutY.height,
      });
    }

    editorAPI.components.layout.moveToBoundingCoordinates(
      selectedComp,
      { y: selectedCompY },
      { dontAddToUndoRedoStack: true },
    );
  };

  // Handles quirks of viewer's push api
  if (Array.isArray(selectedComp) && selectedComp.length > 1) {
    positionComponents();
  } else {
    editorAPI.transactions.run(async () => {
      positionComponents();
    });
  }
}

export {
  getAllSlotsInContainer,
  getAllSlotsForComponent,
  findAvailableSlot,
  openSlot,
};
